import { useEffect, useState } from "react"

export type PanelPresenceState = "open" | "closed"

/** How long the tools panel slides open or closed (matches the CSS duration on the panel wrappers). */
export const PANEL_TRANSITION_MS = 220

/**
 * Keeps a panel mounted while it animates closed, and mounts it one frame before it animates open,
 * so a CSS transition can run in both directions. `mounted` says whether to render the element;
 * `state` is the value to drive the transition from (closed on the first paint, then open).
 */
export function usePanelPresence(open: boolean, durationMs = PANEL_TRANSITION_MS): { mounted: boolean; state: PanelPresenceState } {
  const [mounted, setMounted] = useState(open)
  const [state, setState] = useState<PanelPresenceState>(open ? "open" : "closed")

  useEffect(() => {
    if (open) {
      setMounted(true)
      // Two frames: the first paints the closed position, the second flips it so the transition runs.
      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setState("open"))
      })
      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }
    setState("closed")
    const timer = setTimeout(() => setMounted(false), durationMs)
    return () => clearTimeout(timer)
  }, [durationMs, open])

  return { mounted, state }
}
